import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Input } from '@mui/material'
import {
  type Field,
  PuiSelect,
  type SelectEntity,
} from '@pbt/pbt-ui-components'

import { useAppointmentTypes } from '~/components/common/appointments/useAppointmentTypes'
import NestedMenuItem from '~/components/common/inputs/NestedMenuItem'
import FeatureToggle from '~/constants/featureToggle'
import { getAppointmentTypesMap } from '~/store/reducers/appointmentTypes'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'

export interface AppointmentTypeSelectProps {
  disabled: boolean
  field: Field | undefined
  multiDayEnabled?: boolean
  repeatEnabled?: boolean
}

const applyDisabledToDropOffTypes = (
  items: SelectEntity[],
  disabledIds: Set<string>,
): SelectEntity[] => {
  if (disabledIds.size === 0) {
    return items
  }
  return items.map((item) => {
    const { subitems } = item
    if (subitems?.length) {
      return {
        ...item,
        subitems: subitems.map((sub) =>
          disabledIds.has(sub.id) ? { ...sub, disabled: true } : sub,
        ),
      }
    }
    return disabledIds.has(item.id) ? { ...item, disabled: true } : item
  })
}

const AppointmentTypeSelect = ({
  disabled,
  field,
  multiDayEnabled = false,
  repeatEnabled = false,
}: AppointmentTypeSelectProps) => {
  const [appointmentSelectOpen, setAppointmentSelectOpen] = useState(false)
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null)

  const appointmentTypeItems = useAppointmentTypes()
  const appointmentTypesMap = useSelector(getAppointmentTypesMap)
  const isDropOffEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_DROP_OFF),
  )

  const disabledDropOffIds = useMemo(() => {
    if (!isDropOffEnabled || (!repeatEnabled && !multiDayEnabled)) {
      return new Set<string>()
    }
    const ids = new Set<string>()
    Object.entries(appointmentTypesMap).forEach(([id, type]) => {
      if (type.dropOff === true) {
        ids.add(id)
      }
    })
    return ids
  }, [appointmentTypesMap, isDropOffEnabled, multiDayEnabled, repeatEnabled])

  const itemsWithDropOffTypesDisabled = useMemo(
    () => applyDisabledToDropOffTypes(appointmentTypeItems, disabledDropOffIds),
    [appointmentTypeItems, disabledDropOffIds],
  )

  const isDisplayNameUpdateEnabled = useSelector(
    getFeatureToggle(FeatureToggle.RHAP_APPT_TYPE_NAMING_UPDATES),
  )
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)

  const selectItemProps = {
    onMouseEnter: (id: string) => {
      setActiveMenuItem(id)
    },
    onSelected: () => {
      setAppointmentSelectOpen(false)
    },
    shouldClose: (id: string) => activeMenuItem !== id,
  }

  const renderSelected = (id: string) => {
    for (let i = 0; i < itemsWithDropOffTypesDisabled.length; i++) {
      const item = itemsWithDropOffTypesDisabled[i]

      if (item.id === id) {
        return item.name
      }

      const subitems = (item as SelectEntity).subitems ?? []
      const selected = subitems.find((cur) => cur.id === id)

      if (selected) {
        return `${item.name} ${selected.name}`
      }
    }

    return ''
  }

  return (
    <PuiSelect
      Item={NestedMenuItem}
      disabled={disabled}
      field={field}
      input={<Input id="appointment-type-select" />}
      items={itemsWithDropOffTypesDisabled}
      name="appointmentType"
      open={appointmentSelectOpen}
      renderEmpty={false}
      renderSelectedValue={
        isDisplayNameUpdateEnabled && isOmnichannel ? renderSelected : undefined
      }
      selectItemProps={selectItemProps}
      onClose={() => setAppointmentSelectOpen(false)}
      onOpen={() => setAppointmentSelectOpen(true)}
    />
  )
}

export default AppointmentTypeSelect
